/**
 * Node Hover Context (external-store based)
 *
 * Tracks which node is currently being hovered. Backed by a module-level
 * external store + `useSyncExternalStore` so consumers can subscribe via
 * a SELECTOR — only re-rendering when the *projected value* changes.
 *
 * Why this matters:
 *
 *   1. The setter is needed by every node component (DesignerNodeShell,
 *      GroupNode, ScriptWriter, etc.) on mouseenter/leave. Setter reference
 *      is stable, so consumers of the setter never re-render on hover.
 *
 *   2. Edges (`SimpleEdge`) need to know whether they are CONNECTED to the
 *      hovered node — a per-edge boolean derived from `(hoveredId === source
 *      || hoveredId === target)`. With a React Context that exposed the raw
 *      id, every edge re-rendered on every hover change (even ones whose
 *      boolean answer stayed false). We've seen 50 edges re-render in a
 *      single tick on a moderately-sized project.
 *
 * `useHoveredNodeIdSelector(selector)` calls the selector with the latest
 * hovered id and uses `useSyncExternalStore`'s built-in equality check to
 * skip re-renders when the projected value is unchanged.
 *
 * The legacy hooks (`useNodeHover`, `useNodeHoverState`, `useHoveredNodeId`,
 * `<NodeHoverContext.Provider>`) are kept as thin shims that subscribe to
 * the raw id — DO NOT use them in render-hot paths; they re-render on
 * every hover change.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react'

// ---------------------------------------------------------------------------
// External store
// ---------------------------------------------------------------------------

let _hoveredNodeId: string | null = null
const _listeners = new Set<() => void>()

function _subscribe(cb: () => void): () => void {
  _listeners.add(cb)
  return () => _listeners.delete(cb)
}

function _getSnapshot(): string | null {
  return _hoveredNodeId
}

function _setHoveredNodeId(id: string | null): void {
  if (_hoveredNodeId === id) return
  _hoveredNodeId = id
  for (const cb of _listeners) cb()
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

/** Subscribe to the raw hovered node id. Re-renders on every hover change. */
export function useHoveredNodeId(): string | null {
  return useSyncExternalStore(_subscribe, _getSnapshot, _getSnapshot)
}

/**
 * Subscribe to a derived value of the hovered node id. Only re-renders when
 * the selector's output actually changes (default Object.is comparison;
 * pass a custom isEqual for non-primitives).
 *
 *   const isHovered = useHoveredNodeIdSelector(
 *     (id) => id === source || id === target,
 *   )
 */
export function useHoveredNodeIdSelector<T>(
  selector: (id: string | null) => T,
): T {
  // useSyncExternalStore re-renders only when the snapshot returned by
  // _getServerSnapshot/_getSnapshot changes per Object.is. Wrap so the
  // store snapshot is the projected value.
  const stableGet = useMemo(() => () => selector(_getSnapshot()), [selector])
  return useSyncExternalStore(_subscribe, stableGet, stableGet)
}

/** Stable setter — never causes re-renders by itself. */
export function useSetHoveredNodeId(): (id: string | null) => void {
  return _setHoveredNodeId
}

// ---------------------------------------------------------------------------
// Back-compat surface (LEGACY — re-renders on every hover change)
// ---------------------------------------------------------------------------

export interface NodeHoverContextValue {
  hoveredNodeId: string | null
  setHoveredNodeId: (id: string | null) => void
}

/** Legacy combined-value hook. Re-renders on every hover change. */
export function useNodeHover(): NodeHoverContextValue {
  const hoveredNodeId = useHoveredNodeId()
  const setHoveredNodeId = useSetHoveredNodeId()
  return useMemo(
    () => ({ hoveredNodeId, setHoveredNodeId }),
    [hoveredNodeId, setHoveredNodeId],
  )
}

/** Legacy hook for wiring an inline provider value. */
export function useNodeHoverState(): NodeHoverContextValue {
  const hoveredNodeId = useHoveredNodeId()
  const setHoveredNodeId = useCallback((id: string | null) => {
    _setHoveredNodeId(id)
  }, [])
  return useMemo(
    () => ({ hoveredNodeId, setHoveredNodeId }),
    [hoveredNodeId, setHoveredNodeId],
  )
}

/**
 * Legacy provider shim — the value is ignored (state lives in the module
 * store now). Kept so existing call sites that wrap children in
 * `<NodeHoverContext.Provider value={...}>` keep working without a refactor.
 */
export const NodeHoverContext = {
  Provider({ children }: { value: NodeHoverContextValue; children: React.ReactNode }) {
    return <>{children}</>
  },
}

/**
 * Provider component — kept as a no-op for parity with legacy use. The
 * actual store is module-scoped, so no provider is strictly required.
 */
export function NodeHoverProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}
